import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { PreparedSortCriteria, SortDirection } from '@steelbuy/api-integration';
import { ListingBuyerSearchSortCriteria } from '@steelbuy/ts-shared';
import { useIsVendorFeatureSupported } from '@steelbuy/vendor';

export interface SearchResultsContextValue {
    sortCriteria: PreparedSortCriteria<ListingBuyerSearchSortCriteria>;
    setSortCriteria: (sort: PreparedSortCriteria<ListingBuyerSearchSortCriteria>) => void;
    scrollPosition: number;
    setScrollPosition: (position: number) => void;
    reset: () => void;
}

const defaultSmartMatchingSortCriteria = {
    property: ListingBuyerSearchSortCriteria.RELEVANCE,
    direction: SortDirection.DESCENDING,
};

const defaultSortCriteria = {
    property: ListingBuyerSearchSortCriteria.PRICE,
    direction: SortDirection.ASCENDING,
};

const defaultValue: SearchResultsContextValue = {
    sortCriteria: [defaultSortCriteria],
    setSortCriteria: () => null,
    scrollPosition: 0,
    reset: () => null,
    setScrollPosition: () => null,
};

const getDefaultSortCriteria = (
    isSmartMatchingEnabled: boolean
): PreparedSortCriteria<ListingBuyerSearchSortCriteria> => [
    isSmartMatchingEnabled ? defaultSmartMatchingSortCriteria : defaultSortCriteria,
];

const SearchResultsContext = createContext(defaultValue);

export const SearchResultsContextProvider = ({ children }: { children: ReactNode }) => {
    const isSmartMatchingEnabled = useIsVendorFeatureSupported('SMART_MATCHING');
    const [sortCriteria, setSortCriteria] = useState(getDefaultSortCriteria(isSmartMatchingEnabled));
    const [scrollPosition, setScrollPosition] = useState(defaultValue.scrollPosition);
    const reset = useCallback(() => {
        setSortCriteria(getDefaultSortCriteria(isSmartMatchingEnabled));
        setScrollPosition(defaultValue.scrollPosition);
    }, []);

    const value = useMemo(
        () => ({
            sortCriteria,
            setSortCriteria,
            scrollPosition,
            setScrollPosition,
            reset,
        }),
        [sortCriteria, scrollPosition, setSortCriteria, setScrollPosition, reset]
    );

    return <SearchResultsContext.Provider value={value}>{children}</SearchResultsContext.Provider>;
};

export const useSearchResultsContext = () => useContext(SearchResultsContext);
